import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRENDS_DATE_RANGE, Trends } from 'yankees-react-utils';
import { useOktaQuery, PlayerReport } from '../../../data';
import HitterStrengthsTrendsTopLevelFilter from './HitterStrengthsTopLevelFilter';

const now = new Date();
const DATE_RANGE_MAP = {
    [TRENDS_DATE_RANGE.ONE_MONTH]: new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    [TRENDS_DATE_RANGE.THREE_MONTHS]: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    [TRENDS_DATE_RANGE.ONE_YEAR]: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
    [TRENDS_DATE_RANGE.YTD]: new Date(now.getFullYear(), 0, 1),
    [TRENDS_DATE_RANGE.TWO_YEARS]: new Date(now.getFullYear() - 2, now.getMonth(), now.getDate()),
    [TRENDS_DATE_RANGE.THREE_YEARS]: new Date(now.getFullYear() - 3, now.getMonth(), now.getDate()),
    [TRENDS_DATE_RANGE.FIVE_YEARS]: new Date(now.getFullYear() - 5, now.getMonth(), now.getDate()),
};

let abortController = null;

const HitterStrengthsTrends = ({ playerid, defaultLevelId }) => {
    const { t: translate } = useTranslation();
    const [avgRange, setAvgRange] = useState('30');
    const [swingRange, setSwingRange] = useState('200');
    const [levels, setLevels] = useState([]);
    const [dateRange, setDateRange] = useState(TRENDS_DATE_RANGE.YTD);
    const [hasBlast, setHasBlast] = useState(false);

    const { data: trendsData, isFetching: isFetchingTrends } = useOktaQuery(
        ['trends', playerid, avgRange, swingRange, levels?.filter((l) => l.checked).map((l) => l.value).join(','),
            dateRange],
        async (t) => {
            if (abortController) {
                abortController.abort();
            }
            const levelIds = levels?.filter((l) => l.checked).map((l) => l.value).filter((x) => !!x);
            abortController = new AbortController();
            const allTrends = await PlayerReport.hitmonTrends(t, {
                playerid,
                levelids: levelIds.length ? levelIds : undefined,
                avgrange: avgRange,
                swingrange: swingRange,
                startdate: DATE_RANGE_MAP[dateRange].toISOString().slice(0, 10),
                enddate: now.toISOString().slice(0, 10),
            }, { signal: abortController.signal });
            if (!allTrends.trends.length && dateRange === TRENDS_DATE_RANGE.YTD) {
                setDateRange(TRENDS_DATE_RANGE.ONE_YEAR);
            }
            return allTrends;
        },
        { staleTime: Infinity, enabled: !!levels.length },
    );

    return (
        <div className="text-start">
            <div className="d-flex justify-content-between align-items-center">
                <h3>{translate('sections.reports.hitter.trends')}</h3>
                <HitterStrengthsTrendsTopLevelFilter
                    playerId={playerid}
                    defaultLevelId={defaultLevelId}
                    levels={levels}
                    setLevels={setLevels}
                    avgRange={avgRange}
                    setAvgRange={setAvgRange}
                    swingRange={swingRange}
                    setSwingRange={setSwingRange}
                    hasBlast={hasBlast}
                />
            </div>
            <Trends
                isLoading={isFetchingTrends}
                initialShowAdjustments={false}
                initialShowLevelChange={false}
                trends={trendsData?.trends ?? []}
                blastSwings={trendsData?.blastswings ?? []}
                forcePlateJumps={[]}
                adjustments={trendsData?.adjustments ?? []}
                levelChanges={trendsData?.levelchanges ?? []}
                dateRange={dateRange}
                setDateRange={setDateRange}
                setHasBlast={setHasBlast}
                setHasTrends={() => { }}
                setHasForcePlate={() => { }}
                connectNulls={false}
                defaultStatId={defaultLevelId === 8 ? 'hfx' : 'age-adj'}
                showBatTracking
            />
        </div>
    );
};

export default HitterStrengthsTrends;
